<i18n>
{
	"en": {
		"ata": "ATA",
		"atd": "ATD",
		"eta": "ETA",
		"etd": "ETD",
		"invalidEtd": "Invalid ETD",
		"invalidEta": "Invalid ETA",
		"invalidAtd": "Invalid ATD",
		"invalidAta": "Invalid ATA",
		"etdMustBeBeforeEta": "ETD must be before or same as ETA",
		"atdMustBeBeforeAta": "ATD must be before or same as ATA",
		"time": "Time",
		"signature": "Signature",
		"plannedLoading": "Planned loading",
		"loaded": "Loaded",
		"plannedUnloading": "Planned unloading",
		"unloaded": "Unloaded"
	},
	"no": {
		"ata": "ATA",
		"atd": "ATD",
		"eta": "ETA",
		"etd": "ETD",
		"invalidEtd": "Ugyldig ETD",
		"invalidEta": "Ugyldig ETA",
		"invalidAtd": "Ugyldig ATD",
		"invalidAta": "Ugyldig ATA",
		"etdMustBeBeforeEta": "ETD må være før eller lik ETA",
		"atdMustBeBeforeAta": "ATD må være før eller lik ATA",
		"time": "Tid",
		"signature": "Signatur",
		"plannedLoading": "Planlagt lasting",
		"loaded": "Lastet",
		"plannedUnloading": "Planlagt lossing",
		"unloaded": "Losset"
	}
}
</i18n>
<template>
	<div class="time">
		<div class="xtd">
			<div class="etd">
				<div class="header">
					{{ etdText }}
				</div>

				<div class="date-and-time">
					<t-date-picker
						class="date-picker"
						:useMinWidth="true"
						:value="order.etd && displayDateFormatter(order.etd.date)"
						:dateFormat="displayDateFormat"
						:isInvalid="Boolean(orderErrors.etd)"
						:validationMessage="etdDateErrorMessage"
						disableButtonTabbing
						:disabled="orderIsLocked"

						@input="$emit('changeEtdDate', $event)"
					/>

					<t-time-picker
						:useMinWidth="true"
						:value="etdTime"
						:placeholder="$t('time')"
						disableButtonTabbing
						:disabled="orderIsLocked"

						@input="$emit('changeEtdTime', $event)"
					/>
				</div>
			</div>

			<div class="atd">
				<div class="header">
					{{ atdText }}
				</div>

				<div class="date-and-time">
					<t-date-picker
						class="date-picker"
						:useMinWidth="true"
						:value="order.atd && displayDateFormatter(order.atd.date)"
						:dateFormat="displayDateFormat"
						:isInvalid="Boolean(orderErrors.atd)"
						:validationMessage="atdDateErrorMessage"
						:disabled="order.atdIsLocked || orderIsLocked"
						disableTabbing
						disableTimeoutForEnterEvent

						@input="$emit('changeAtdDate', $event)"
					/>

					<t-time-picker
						ref="atdTimePicker"
						:useMinWidth="true"
						:value="atdTime"
						:placeholder="$t('time')"
						:disabled="order.atdIsLocked || orderIsLocked"
						disableTabbing
						disableTimeoutForEnterEvent

						@input="$emit('changeAtdTime', $event)"
					/>
				</div>
			</div>
		</div>

		<div class="signature-container">
			<div v-if="atdSignature" class="header">
				{{ $t('signature') }}
			</div>

			<Signature
				v-if="atdSignature"
				:url="atdSignature.url"
				:name="atdSignature.name"
			/>
		</div>

		<div class="xta">
			<div class="eta">
				<div class="header">
					{{ etaText }}
				</div>

				<div class="date-and-time">
					<t-date-picker
						class="date-picker"
						:useMinWidth="true"
						:value="order.eta && displayDateFormatter(order.eta.date)"
						:minDate="getDefaultMomentDate(order.etd.date)"
						:dateFormat="displayDateFormat"
						:isInvalid="Boolean(orderErrors.eta)"
						:validationMessage="etaDateErrorMessage"
						disableButtonTabbing
						:disabled="orderIsLocked"

						@input="$emit('changeEtaDate', $event)"
					/>

					<t-time-picker
						:useMinWidth="true"
						:value="etaTime"
						:placeholder="$t('time')"
						disableButtonTabbing
						:disabled="orderIsLocked"

						@input="$emit('changeEtaTime', $event)"
					/>
				</div>
			</div>

			<div class="ata">
				<div class="header">
					{{ ataText }}
				</div>

				<div class="date-and-time">
					<t-date-picker
						ref="ataDatePicker"
						class="date-picker"
						:useMinWidth="true"
						:value="order.ata && displayDateFormatter(order.ata.date)"
						:dateFormat="displayDateFormat"
						:isInvalid="Boolean(orderErrors.ata)"
						:validationMessage="ataDateErrorMessage"
						:disabled="order.ataIsLocked || orderIsLocked"
						disableTabbing
						disableTimeoutForEnterEvent

						@input="$emit('changeAtaDate', $event)"
					/>

					<t-time-picker
						ref="ataTimePicker"
						:useMinWidth="true"
						:value="ataTime"
						:placeholder="$t('time')"
						:disabled="order.ataIsLocked || orderIsLocked"
						disableTabbing
						disableTimeoutForEnterEvent

						@input="$emit('changeAtaTime', $event)"
					/>
				</div>
			</div>
		</div>

		<div
			class="signature-container"
		>
			<div v-if="ataSignature" class="header">
				{{ $t('signature') }}
			</div>

			<Signature
				v-if="ataSignature"
				:url="ataSignature.url"
				:name="ataSignature.name"
			/>
		</div>
	</div>
</template>

<script>
import {
	displayDateFormat,
	displayDateFormatter,
	storageDateFormat
} from '@/core';
import moment from 'moment';
import Signature from '@/modules/newOrder/common/Signature';
import {
	mapGetters
} from '@/modules/newOrder/store/newOrder.store';

export default {
	name: 'Time',
	components: { Signature },
	props: {
		order: {
			type: Object,
			required: true
		},
		orderErrors: {
			type: Object,
			required: true
		},
		atdSignature: {
			type: Object,
			default: null,
		},
		ataSignature: {
			type: Object,
			default: null,
		},
		isSubOrder: Boolean,
		isFirstSubOrder: Boolean,
		isLastSubOrder: Boolean,
	},
	computed: {
		...mapGetters(['orderIsLocked']),

		etdText() {
			if (!this.isSubOrder)
				return 'ETD';

			if (this.isFirstSubOrder)
				return 'ETD';

			return this.$t('plannedLoading');
		},
		etaText() {
			if (!this.isSubOrder)
				return 'ETA';

			if (this.isLastSubOrder)
				return 'ETA';

			return this.$t('plannedUnloading');
		},
		atdText() {
			if (!this.isSubOrder)
				return 'ATD';

			if (this.isFirstSubOrder)
				return 'ATD';

			return this.$t('loaded');
		},
		ataText() {
			if (!this.isSubOrder)
				return 'ATA';

			if (this.isLastSubOrder)
				return 'ATA';

			return this.$t('unloaded');
		},

		displayDateFormat: () => displayDateFormat,
		etdTime() {
			if (!this.order.etd || !this.order.etd.hasTime) return null;

			return this.order.etd.date ?
				moment(this.order.etd.date, storageDateFormat).format('LT') :
				undefined;
		},
		atdTime() {
			if (!this.order.atd || !this.order.atd.hasTime) return null;

			return this.order.atd.date ?
				moment(this.order.atd.date, storageDateFormat).format('LT') :
				undefined;
		},
		etaTime() {
			if (!this.order.eta || !this.order.eta.hasTime) return null;

			return this.order.eta.date ?
				moment(this.order.eta.date, storageDateFormat).format('LT') :
				undefined;
		},
		ataTime() {
			if (!this.order.ata || !this.order.ata.hasTime) return null;

			return this.order.ata.date ?
				moment(this.order.ata.date, storageDateFormat).format('LT') :
				undefined;
		},
		etdDateErrorMessage() {
			return (
				this.orderErrors.etd === 'date1IsNotBeforeDate2' && this.$t('etdMustBeBeforeEta')
				|| this.$t('invalidEtd')
			);
		},
		etaDateErrorMessage() {
			return (
				this.orderErrors.eta === 'date1IsNotBeforeDate2' && this.$t('etdMustBeBeforeEta')
				|| this.$t('invalidEta')
			);
		},
		atdDateErrorMessage() {
			return (
				this.orderErrors.atd === 'date1IsNotBeforeDate2' && this.$t('atdMustBeBeforeAta')
				|| this.$t('invalidAtd')
			);
		},
		ataDateErrorMessage() {
			return (
				this.orderErrors.ata === 'date1IsNotBeforeDate2' && this.$t('atdMustBeBeforeAta')
				|| this.$t('invalidAta')
			);
		}

	},
	methods: {
		getFirstInputElementByRef(ref) {
			return ref.$el.querySelector('input');
		},
		displayDateFormatter,
		getDefaultMomentDate(date) {
			return moment(date, storageDateFormat);
		},
	}
};
</script>

<style lang="scss">
.time {
	.date-and-time {
		input {
			padding: 1px 8px;
		}
	}
}

</style>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.time {
	width: 1000px;
	display: flex;
	flex-direction: column;

	.xta {
		margin-top: 7px;
	}

	.xtd, .xta {

		display: flex;
		margin-bottom: 5px;

		.date-and-time {
			display: flex;
		}

		.etd, .atd, .signature-container, .eta, .ata, .date-picker, .time-picker {
			display: inline-block;
		}

		.date-picker {
			margin-right: 5px;
		}

		.etd, .eta {
			margin-right: 8px;
		}
	}

	.signature-container {
		display: inline-block;

		margin-left: 203px;

	}


	.header {
		user-select: none;
		font-size: 11px;
		font-weight: 600;
		line-height: 1.45;
		letter-spacing: 0.3px;
		color: get-color($text, 'dark');
		text-transform: uppercase;
		margin-bottom: 3px;
		margin-left: 1px;
	}
}

</style>
